import { ColDef } from 'ag-grid-community';
import { WidgetConfigurationObj, WidgetConfigurationItemObj, Baseline, VisionStepPerimeter, Measure, VisionKey } from '@wizeoo/akm-message-lib';
import { WizCollectionUtils } from '@wizeoo/akm-core-lib';
import { WizAgGridUtils, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { AkmFDVCell, MathUtils, WizConstants, KeyPair, WidgetLine, FIELD_KIND, FeiKeyDef, MeasureVO, KEY_KIND, BaselineEngine, MeasMode, DataEngine, MeasureEngine } from '@wizeoo/akm-compute-lib';
import { IFreedataWidget, IWidget } from '../widgets/i-widget';
import { Model } from '../../model/model';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { FreeDataVO } from '../../vo/core/freedata.vo';
import { ViewData } from '../../model/view.data';
import { VisionKeyVO } from '../../vo/vision/visionkey.vo';
import { VisionFilterKeyVO } from '../../vo/vision/visionfilterkey.vo';
import { VisionStepPerimeterVO } from '../../vo/vision/visionstepperimeter.vo';
import { WIDGET_GLOBAL_KIND, WIDGET_KIND } from '../../model/enums';
import { ColumnHeaderVO } from '../../framework/components/grid/column-header.vo';
import { AggregateEngine } from '../computer/aggregate-engine';
import { FreedataArgsVO } from '../../vo/core/freedataargs.vo';
import { ChronocodeVO } from '../../vo/core/chronocode.vo';
import { PivotFunction } from '../baselines/pivot-function.obj';
import { getDefaultWidgetConfigurationItemObj, getDefaultWidgetConfigurationObj } from '../widgets/widget.engine';
import { PivotHeaderRenderer } from '../../components/widgets/pivot/renderers/header/pivot-header.renderer';
import { PivotIncExcRenderer } from '../../components/widgets/pivot/renderers/cells/pivot-incexc.renderer';
import { FreedataValueVO } from '../../vo/core/freedatavalue.vo';
import { StyleEngine } from './style.engine';
import { ScenarioVO } from '../../vo/scenarii/scenario.vo';

/**
 * Gestion des fonctionalités sur FEI.
 */

// Structure de données d'une colonne de l'ag-grid
export interface WizColDef extends ColDef {
    fkd?: FeiKeyDef;
    widget?: IWidget; // surcharge, on passe le pivot
    wcio?: WidgetConfigurationItemObj; // la conf
    place?: string; // l'emplacement
    enableSorting?: boolean;
    dataformat?: string;
    popEditAllowed?: boolean;
    styleRenderer?: string; // style conditionnel
    defaultStyleRenderer?: string; // style par défaut sur la colonne
    defaultAggStyleRenderer?: string; // style par défaut sur la colonne pour les aggrégats
    datakind?: string;
    fdvfield?: string;
    widgetLine?: WidgetLine; // en mode pivot.revert, la colonne porte la widgetLine
    filter?: boolean; // dans un filter, non éditable
}

/** méthode utilitaires pour manipulation des Feis */
export class FeiEngine  {

  public static TOOL_MODE_INC: string = 'INCEXC'; // mode de création include / exclude
  public static TOOL_MODE_VSP: string = 'VSP'; // mode de création perimeters
  public static TOOL_MODE_LIST: string = 'LIST'; // mode de rendu des mesures de type liste

  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }

  protected get model(): Model {
    return this._model as Model;
  }
  protected set model(value: Model) {
      this._model = value;
  }

  /**
   * alimentation de la structure d'un fake FEI
   * - alimentation des clés
   * - alimentation des lines
   * @param fei 
   */
  public buildToolFEIKeys(fei: FeiVO, toolMode: string, lines: Baseline[]): void {
    
    // construction des clés
    let visionKeys: FeiKeyDef[] = [];
    let filterKeys: FeiKeyDef[] = [];
    let keys: FeiKeyDef[] = [];
    // on collecte les clés de la vision pour le step
    if (fei.vision.visionKeys) {
      // on ajoute les clés de vision
      Object.values(fei.vision.visionKeys || {})
        .sort((a, b) => a.order - b.order)
        .forEach((item: VisionKey) => {
          if (fei.visionStep?.level >= item.stepLevel) { // il y a un step et le niveau correspond 
            let keydef = BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.KEY, item).setParams(false, true)
            visionKeys.push(keydef);
            keys.push(keydef);
          }
        });
    }


    if (toolMode === FeiEngine.TOOL_MODE_INC) { // mode include /exclude
      const fakeMeas: MeasureVO = this.model.measureEngine.buildFake();
      fakeMeas.code = FeiEngine.TOOL_MODE_INC;
      fakeMeas.shortCode = FeiEngine.TOOL_MODE_INC;
      fakeMeas.mode = WizConstants.MEASURE_MODE_INTERACT; // interact, donc présentée par défaut
      fakeMeas.datakind = FIELD_KIND.BOOLEAN; // on veut une checkbox en rendu
      let fake: FeiKeyDef = BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.MEASURE, fakeMeas.source, WizConstants.MEASURE_MODE_INTERACT).setParams(true, true, null).setSize(80,80);
      fake.incexcField = true;
      keys.push(fake);
    } else if (toolMode === FeiEngine.TOOL_MODE_VSP) { // mode création de perimeter
      const fakeMeas: MeasureVO = this.model.measureEngine.buildFake();
      fakeMeas.code = FeiEngine.TOOL_MODE_VSP;
      fakeMeas.shortCode = FeiEngine.TOOL_MODE_VSP;
      fakeMeas.mode = WizConstants.MEASURE_MODE_INTERACT; // interact, donc présentée par défaut
      fakeMeas.editable = true;
      let fake: FeiKeyDef = BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.MEASURE, fakeMeas.source, WizConstants.MEASURE_MODE_INTERACT).setParams(true, true, null).setSize(50,50);
      fake.vspField = true;
      keys.push(fake);
    } else{

    }
    fei.visionKeys = visionKeys; // ordonnées
    fei.filterKeys = filterKeys; // vide...
    fei.keys = keys; // on alimente le FEI
    fei.keyMap = new Map(keys.map(key => [key.fullCode, key]));

    // construction des lines
    fei.lines = new Map();
    fei.lines.set(fei.scenario, new Map());
    lines.forEach((bl: Baseline) => {
      fei.lines.get(fei.scenario).set(bl.key,  this.baselineToFakeWidgetLine(fei, fei.scenario, bl));
    });

    fei.computed = true;
  }

  public baselineToFakeWidgetLine(fei: FeiVO, scenario: string, bl: Baseline): WidgetLine {
    // construction de la ligne
    return {
      key: bl.key, 
      uk: [scenario, bl.key].join(WizConstants.KEY_SEPARATOR),
      binKey: null,
      level: bl.level,
      scen: scenario,
      bl,
      cells: new Map()
    }
  }

  /**
   * construction des colonnes des grids utilitaires (perimeters & incexc)
   * @param widget 
   * @param toolMode 
   * @returns 
   */
  public buildToolGridColumns(widget: IWidget): any[] {

    let keys: FeiKeyDef[] = widget.fei.keys;
    // console.log('feiEngine buildToolGridColumns', widget.fei.uniqueName);
    const rowCols: WizColDef[] = [];
    const dataCols: WizColDef[] = [];
    let keyMap: Map<string, FeiKeyDef> = new Map();
    if(!!keys)  {
      keyMap = new Map(keys.map((item) => [item.fullCode, item]));
    }
    
    const fei: FeiVO = widget.fei;
    // if(widget.context.r.length > 0) {
    widget.context?.r?.forEach(wcio => {
      let col = this.buildGridColumn(keyMap.get(wcio.cod), widget, wcio, 'row', false);
      col.headerComponentParams = { enableMenu:false}; // neutralisation de la conf
      rowCols.push(col);
    });

    widget.context?.d?.forEach(wcio => {
      let col = this.buildGridColumn(keyMap.get(wcio.cod), widget, wcio, 'data', true);
      col.headerComponentParams = { enableMenu:false}; // neutralisation de la conf
      dataCols.push(col);
    });


    let result = rowCols.concat(dataCols);
    return result;
  }

  /* ##############################################################################
            construction des lignes
     ############################################################################## */

  /**
   * construction des lignes: pour un widget
   * retour le pool simple ou le pool agrégé selon le context.pm
   * @param widget source
   * @returns un pool de WidgetLine
   */
  public buildRows(widget: IWidget): WidgetLine[] {
    let fei: FeiVO = widget.fei as FeiVO; // le fei
    let context: WidgetConfigurationObj = widget.context; // le contexte
    
    // collection de retour, par défaut, tout, à plat
    let result: WidgetLine[] = fei.getAllLines(); 


    // ####
    // gestion des droits Read sur step / stepPerimeters pour définition du pool utilisable
    // ####
    const baseCode: string = [fei.visionCode, fei.visionStepLevel].join(ViewData.KEY_SEPARATOR);
    
    const perimeters: VisionStepPerimeterVO[] = []; // sélection de mes périmètres et alimentation de ma map de vspKeys
    this.model.visionEngine.getStepPerimetersByLevel(fei.vision, fei.visionStepLevel).forEach((vsp: VisionStepPerimeter) => {
      if (this.model.permissionEngine.canRead(baseCode + ViewData.KEY_SEPARATOR + vsp.code)) {
        let perimVO: VisionStepPerimeterVO = new VisionStepPerimeterVO(vsp);
        perimeters.push(perimVO);
        fei.vspKeysMap = new Map([...fei.vspKeysMap, ...perimVO.keyMap]);
      }
    });

    if (!this.model.permissionEngine.canRead(baseCode)) { // j'ai pas le step... ça commence mal
      if (fei.vspKeysMap.size > 0) { // j'ai un vsp
        result = result.filter(item => fei.vspKeysMap.has(item.key)); // je filtre
        // console.log('vsp', result);

      } else { // je n'ai rien... je clear le result
        result = [];
        // console.log('rien', result);
      }
    }

    // ####
    // restrictions en cas de master
    // ####
    if (!!widget.context?.muid) {
      if (widget.widgetKind === WIDGET_KIND.FD_GRID || 
          widget.widgetKind === WIDGET_KIND.FD_FORM || 
          widget.widgetKind === WIDGET_KIND.FD_TILE || 
          widget.widgetKind === WIDGET_KIND.FD_CALENDAR || 
          widget.widgetKind === WIDGET_KIND.GENERATOR) {
        // TODO, voir ce qu'il est possible de faire
      } else {
        // restrictions sur le pool du maître // il a de toute façon géré lui-même son perimeter
        result = this.model.widgetEngine.availableMasterLines(widget, result);
      }
    }

    // ####
    // construction de l'aggregat en mode pivot
    // ####
    if (!!context?.pm) { // mode pivot, construction des agrégats
      const keyMap: Map<string, WidgetLine> = new Map<string, WidgetLine>(); // construction des lignes uniqueKey = WidgetLine
      let scenKeyKey: string;

      let level: number = 0; // pour gestion du gigogne
      let maxLevel: number = 0; // nbr de clés pour le gigogne
      let pool: WidgetConfigurationItemObj[] = []; // capture du nbr de clé pour les regroupements gigognes

      if(!!context.pmdex) { // totaux gigognes
        pool = context.r;
        maxLevel = pool.length;
        level = maxLevel;
      }

      // va donner les éléments de plus bas niveau. Recycler pour chaque tour
      // ajouter les subTot dans Keymap
      let levelLines: WidgetLine[] = result;
      let levelKeyMap: Map<string, WidgetLine>; // construction des lignes uniqueKey = WidgetLine

      let goodLines: WidgetLine[] = [];
      let bottomGoodLines: WidgetLine[] = [];
      let newMaster: WidgetLine;
      do {
        // pour chaque level en commençant par le bas
        levelKeyMap = new Map<string, WidgetLine>();

        levelLines.forEach((wl: WidgetLine) => {     
            // ça c'est la clé d'aggrégat de la ligne
            const uniqueKey: string = this.model.widgetEngine.generateAggregateLineKey(widget, wl, level);
            // console.log('uniqueKey', uniqueKey);
            // création des clés pour chaque level (selon max level)

            if (!levelKeyMap.has(uniqueKey)) { // existe pas. On crée la masterLine
              newMaster = BaselineEngine.buildMasterLine(uniqueKey, wl);
              newMaster.subTotalLevel = level; // ce sont les lignes d'aggrégat simple, pas encore consolidées
              
              if(level > 0) { // on a besoin d'aggréger
                newMaster.subTotalKeySet = new Set();
                // alimentation des fullCodes qui composent cette clé
                pool = [...context.r].slice(0, level);

                pool.forEach((wcio: WidgetConfigurationItemObj) => {
                  let fkd: FeiKeyDef = fei.keyMap.get(wcio.cod);
                  newMaster.subTotalKeySet.add(fkd.fullCode);
                });
              }

              levelKeyMap.set(uniqueKey, newMaster); // on ajoute la ligne

              //console.log('levelKeyMap', uniqueKey);
            } else { // existe. On ajoute la ligne à l'existante
              scenKeyKey = [wl.scen, wl.key].join(WizConstants.FD_PARTS_SEPARATOR);
              levelKeyMap.get(uniqueKey).ch.set(scenKeyKey, wl);
            }
        });

        // on a ajouté tout un niveau. Maintenant, éventuelle consolidation
        // console.log('uniqueKeys', levelKeyMap);

        // on garde ces lignes
        goodLines = [...goodLines, ...levelKeyMap.values()];
        if(level === maxLevel) { // mémorisation des lignes de plus bas niveau pour mode pmd
          bottomGoodLines = [...levelKeyMap.values()];
        }

        levelLines = [...levelKeyMap.values()];
        level--;
        // [...levelKeyMap.keys()].forEach(k => {
        //   sub = levelKeyMap.get(k);
        //   if(!!sub.ch) {
        //     let subs: WidgetLine[] = [...sub.ch.values()];
        //     levelLines.push(...subs);
        //   }
        //   keyMap.set(k, sub);
        // });

        // level++;


      } while(level > 0);


      
      // ici, on a les lignes de lvl1 en cas de pmdex, il faut générer les sous-lignes de cumul
      // console.log('keyMap', keyMap);
      
      result = goodLines; // Array.from(keyMap.values());
      // console.log('levelLines', result, bottomGoodLines);
      if(widget.context.pmd) { // affichage des lignes de détail pour un pivot
        //result = [];
        bottomGoodLines.forEach(wl => {
        
        //[...levelKeyMap.values()].forEach(wl => {
        //[...keyMap.values()].forEach(wl => {
          
          // result.push(wl);
          [...wl.ch?.values()|| []].forEach(swl => {
            result.push(swl);
          });
        });

        // console.log('pmd', result);

      } else {
        
      }
    } 
    // console.log('result', result);
    return result;
  }

  /* ##############################################################################
            construction des colonnes
     ############################################################################## */

  /**
   * Méthode d'entrée pour constructions des objets colonnes pour le grid des FreedataValues
   * Va créer les colonnes par appel à buildGridColumn
   * Puis assembler dans les entêtes par appel à buildGridHeader
   * @param widget: IWidget
   * @param editionAllowed, autoriser l'accès au crayon (neutralisé pour les popUps)
   * @param hideExport mode generator, pas de reprise des colonnes non exportables
   * @returns une collection d'objet colonnes pour le grid
   */
  public buildFDGridColumns(widget: IFreedataWidget, context: WidgetConfigurationObj, editionAllowed: boolean = true, hideExport: boolean = false): any[] {
    
    const rowCols: WizColDef[] = [];

    // construction des groupes si existe
    let useGroup: boolean = false;
    let customRowHeader: any[] = []; // colonnes custom
    let customRowHeaderMap: Map<string, any> = new Map();
    let validHeader: Set<string> = new Set();
    let result: any[] = [];
    // création des groupes
    if (context.g?.length > 0) { 
      useGroup = true;
      //customHeader = []; // contient une collection de "header AG" gigogne (pour alimentation du grid)
      const pendingHeader = []; // les groupes membres d'un groupe, on attend papa
      //customHeaderMap = new Map();
      context.g.forEach((wcio: WidgetConfigurationItemObj) => {
        const head: any = {};
        head.headerName = wcio.lbl ? wcio.lbl : wcio.cod;
        head.headerGroupComponent = 'pivotHeaderGroupRenderer';
        head.style = wcio.hsty; // prise du style
        head.textAlign = wcio.heada; // prise de l'alignement
        
        head.fdvfield = wcio.fdvf; // présence d'un champ de rendu
        head.fmt = wcio.fmt; // présence d'un format dans le contexte

        // le libellé du fkd + la valeur de la clé;
        head.groupId = wcio.cod;
        head.columnGroupShow = 'open'; // par défaut pas visible en mode closed
        head.children = [];
        if (!!context.gps) { // marquer les colonnes expanded
          context.gps.forEach(item => {
            if (item.groupId === head.groupId) {
              // console.log('exp', item);
              head.expanded = item.open;
            }
          });
        }

        if (!!wcio.pgrp) { // mon groupe dépend d'un groupe, j'ajoute pas tout de suite
          head.parentID = wcio.pgrp;
          pendingHeader.push(head);
        } else {
          customRowHeader.push(head);
        }
        customRowHeaderMap.set(wcio.cod.split(ViewData.CONTEXT_SEPARATOR)[0], head);
      });

      // 2ème passe, on alimente les sous-groupes
      if (pendingHeader.length > 0) {
        pendingHeader.forEach(child => {
          const daddy = customRowHeaderMap.get(child.parentID);
          if (!daddy) {
            // console.log('col no dad', child.parentID, customHeaderMap);
          } else {
            if(daddy.children.length === 0) {
              child.columnGroupShow = null; // je suis le premier, toujours visible
            }
            daddy.children.push(child);
          }
        });
      }
    }



    context.r.forEach((wcio: WidgetConfigurationItemObj) => {
      // création des colonnes "vraies"
      if(!hideExport) {
        rowCols.push(this.buildGridColumn(widget.fkd, widget, wcio, 'row', editionAllowed));
      } else { // mode export, filtrer les exportables
        if(!!wcio.exportable) {
          rowCols.push(this.buildGridColumn(widget.fkd, widget, wcio, 'row', editionAllowed));
        }
      }
    });

    // s'il pas de groupe, c'est OK
    if(!useGroup) {
      return rowCols;
    }

    // Groupes on ventile les rows dans les bons groupes
    rowCols.forEach((col: any) => {
      col.custom = true;
      if (!!col.wcio.pgrp) {//} && !widget.pivotMode) {
        const head: any = customRowHeaderMap.get(col.wcio.pgrp);
        if (!!head) {
          if(!validHeader.has(col.wcio.pgrp)) { // TODO gérér multi-level
            validHeader.add(col.wcio.pgrp);
            result.push(head);
          }
          if (head.children.length === 0) {
            col.columnGroupShow = null;
          } else {
            col.columnGroupShow = 'open';
          }
          head.children.push(col);
        } else {
          result.push(col);
        }
      } else { // pas de colonnes customs
        result.push(col);
      }
    });
    return result; 
  }

  /**
   * va tenter d'alimenter les colonnes custom
   * @param widget
   * @param keyMap 
   * @param context 
   * @param customHeader 
   * @param customHeaderMap 
   * @param dataCols pour leur assigner le groupe parent
   * @param editionAllowed
   */
  private customGroupBuilder(widget: IWidget, keyMap: Map<string, FeiKeyDef>, 
    context: WidgetConfigurationObj, editionAllowed: boolean ): any[]
    {
        let customHeader: any[] = []; // colonnes custom
        let customHeaderMap: Map<string, any> = new Map();
        let validHeader: Set<string> = new Set();
        let result: any[] = [];
      
      
        // création des groupes
        if (context.g?.length > 0) { 
          //customHeader = []; // contient une collection de "header AG" gigogne (pour alimentation du grid)
          const pendingHeader = []; // les groupes membres d'un groupe, on attend papa
          //customHeaderMap = new Map();
          context.g.forEach((wcio: WidgetConfigurationItemObj) => {
            const head: any = {};
            head.headerName = wcio.lbl ? wcio.lbl : wcio.cod;
            head.headerGroupComponent = 'pivotHeaderGroupRenderer';
            head.style = wcio.hsty; // prise du style
            head.textAlign = wcio.heada; // prise de l'alignement
            
            head.fdvfield = wcio.fdvf; // présence d'un champ de rendu
            head.fmt = wcio.fmt; // présence d'un format dans le contexte

            // le libellé du fkd + la valeur de la clé;
            head.groupId = wcio.cod;
            head.columnGroupShow = 'open'; // par défaut pas visible en mode closed
            head.children = [];
            if (!!context.gps) { // marquer les colonnes expanded
              context.gps.forEach(item => {
                if (item.groupId === head.groupId) {
                  // console.log('exp', item);
                  head.expanded = item.open;
                }
              });
            }

            if (!!wcio.pgrp) { // mon groupe dépend d'un groupe, j'ajoute pas tout de suite
              head.parentID = wcio.pgrp;
              pendingHeader.push(head);
            } else {
              customHeader.push(head);
            }
            customHeaderMap.set(wcio.cod.split(ViewData.CONTEXT_SEPARATOR)[0], head);
          });

          // 2ème passe, on alimente les sous-groupes
          if (pendingHeader.length > 0) {
            pendingHeader.forEach(child => {
              const daddy = customHeaderMap.get(child.parentID);
              if (!daddy) {
                // console.log('col no dad', child.parentID, customHeaderMap);
              } else {
                if(daddy.children.length === 0) {
                  child.columnGroupShow = null; // je suis le premier, toujours visible
                }
                daddy.children.push(child);
              }
            });
          }
        }

        // flag pour ajout d'une seule colonne permanente sur les groupes
        let fdCol: boolean = true;
        
        // création des colonnes de données. Potentiellement inféodées à des Header (si champs en colonne)
        context.d.forEach((wcio: WidgetConfigurationItemObj) => {
            if (keyMap.has(wcio.cod)) {
              wcio.fdCol = fdCol; // colonne expandable
              fdCol = false; // une seule
              const col: any = this.buildGridColumn(keyMap.get(wcio.cod), widget, wcio, 'data', editionAllowed);
              col.custom = true;
              if (customHeader.length > 0) {//} && !widget.pivotMode) {
                const head: any = customHeaderMap.get(wcio.pgrp);
                if (!!head) {
                  if(!validHeader.has(wcio.pgrp)) { // TODO gérér multi-level
                    validHeader.add(wcio.pgrp);
                    result.push(head);
                  }
                  if (head.children.length === 0) {
                    col.columnGroupShow = null;
                  } else {
                    col.columnGroupShow = 'open';
                  }
                  head.children.push(col);
                } else {
                  result.push(col);
                }
              } else { // pas de colonnes customs
                result.push(col);
              }
            }
        });

        return result;
    }

 /**
  * Méthode d'entrée pour constructions des objets colonnes pour le GRID (pas PIVOT)
  * Va créer les colonnes par appel à buildGridColumn
  * Puis assembler dans les entêtes par appel à buildGridHeader
  * @param widget: IWidget
  * @param editionAllowed, autoriser l'accès au crayon (neutralisé pour les popUps)
  * @returns une collection d'objet colonnes pour le grid
  */
  public buildGridColumns(widget: IWidget, context: WidgetConfigurationObj, editionAllowed: boolean = true): any[] {
    // let context: WidgetConfigurationObj = widget.context;
    let keys: FeiKeyDef[] = widget.fei.keys;

    let rowCols: WizColDef[] = [];
    let dataCols: WizColDef[] = [];
    let keyMap: Map<string, FeiKeyDef> = new Map();
    if(!!keys)  {
      keyMap = new Map(keys.map((item) => [item.fullCode, item]));
    }

    // const customRowHeader: WizColDef[] = [];
    // const customRowHeaderMap: Map<string, any> = new Map(); // colonnes custom
    // context.r.forEach((wcio: WidgetConfigurationItemObj) => {
    //   // création des colonnes "vraies"
    //   if (keyMap.has(wcio.cod)) {
    //     rowCols.push(this.buildGridColumn(keyMap.get(wcio.cod), widget, wcio, 'row', editionAllowed));
    //   }
    // });

    rowCols = this.customRowGroupBuilder(widget, keyMap, context, editionAllowed);
    // if(customRowHeader.length >0) { // groupe sur les lignes
    //   rowCols = customRowHeader;
    // }

    // rowCols.forEach(wcd => {
    //   wcd.flex = 1;
    // });

    //const fei: FeiVO = widget.fei as FeiVO;
    // let customHeader: any[] = []; // colonnes custom
    // let customHeaderMap: Map<string, any> = new Map(); // colonnes custom

    dataCols = this.customGroupBuilder(widget, keyMap, context, editionAllowed);

    let result = rowCols.concat(dataCols);


    // if (customHeader.length > 0) { // colonnes custom, on a fini
    //   result = rowCols.concat(customHeader)
    // } else {
    //   result = rowCols.concat(dataCols);
    // }
    // console.log('heads', result, customHeader); // TODO ici, en cas de parent avec 1 seul enfant ??
    return result;
  }

  /**
   * Méthode d'entrée pour constructions des objets colonnes pour les PIVOTS
   * Va créer les colonnes par appel à buildGridColumn
   * Puis assembler dans les entêtes par appel à buildGridHeader
   * @param widget: IWidget
   * @param editionAllowed, autoriser l'accès au crayon (neutralisé pour les popUps)
   * @returns une collection d'objet colonnes pour le grid
   */
  public buildPivotColumns(widget: IWidget, context: WidgetConfigurationObj, editionAllowed: boolean = true): any[] {

    const fei: FeiVO = widget.fei as FeiVO;
    // let context: WidgetConfigurationObj = widget.context;
    let keys: FeiKeyDef[] = widget.fei.keys;

    let rowCols: WizColDef[] = [];
    let dataCols: WizColDef[] = [];
    let keyMap: Map<string, FeiKeyDef> = new Map();
    if(!!keys)  {
      keyMap = new Map(keys.map((item) => [item.fullCode, item]));
    }

    // flag pour ajout d'une seule colonne pour les groupes de lignes
    let fdRow: boolean = true;

    // colonne de consolidation en mode pivot
    let uniqueColumn: boolean = context.pmdex && !!this.model.widgetEngine.pmdExParams(context)?.uniqueColumn;
    let uniqueDone: boolean = false;


    if(uniqueColumn) {
      context.r.forEach((wcio: WidgetConfigurationItemObj) => {
            // création des colonnes "vraies"
            if (keyMap.has(wcio.cod)) {
              wcio.fdCol = fdRow; // colonne expandable
              fdRow = false; // une seule
              let rc = this.buildGridColumn(keyMap.get(wcio.cod), widget, wcio, 'row', false);
              
              // gestion colonne unique en mode pmdEx
              if(uniqueColumn) {
                if(uniqueDone) {
                  rc.hide = true; // on masque les autres colonnes
                }
                uniqueDone = true;
              } 
              rowCols.push(rc);
            }
          });

    } else {
      rowCols = this.customRowGroupBuilder(widget, keyMap, context, editionAllowed);
    }
    
    // console.log('pivot rows', rowCols);

    // flag pour ajout d'une seule colonne permanente sur les groupes
    let fdCol: boolean = true;

    context.d.forEach((wcio: WidgetConfigurationItemObj) => {
      // création des colonnes de données. Potentiellement inféodées à des Header (si champs en colonne)
      if (keyMap.has(wcio.cod)) {
        wcio.fdCol = fdCol; // colonne expandable
        fdCol = false; // une seule
        const col: any = this.buildGridColumn(keyMap.get(wcio.cod), widget, wcio, 'data', editionAllowed);
        col.custom = true;     
        dataCols.push(col);
      }
    });

    // collecte des colonnes requise.
    // application du filtre sur la source du fei
    let heads: ColumnHeaderVO[] = null; // tableau d'entêtes sous la forme coll( [fkd.code], col[keyRawVal, KeyValue, sortKey] )
  
    // les paires label/sortKey disponibles pour les colonnes
    // champ de la colonne (code#level) = rawVal du FDV / chrono = KeyPair(label + sort)
    const byColumnPairs: Map<string, Map<string, KeyPair>> = new Map();

    // le format pour les colonnes (si mesure)
    const byColumnFormats: Map<string, string> = new Map();

    context.c.forEach((wcio: WidgetConfigurationItemObj) => {
      byColumnPairs.set(wcio.cod, new Map());
      byColumnFormats.set(wcio.cod, wcio.fmt);
    });

    // collecte du pool de données, filtré (global + local)
    let pool : WidgetLine[] = [...fei.getAllLines()].filter((item) => widget.mainFilterFunction(item));
    
    // possible filtre supplémentaire sur le master (si son filtre possède mes colonnes)
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(widget);
    
    if(!!master) { // contrôle par filtre global
      pool = this.model.widgetEngine.availableMasterLines(widget, pool);
    }

    let cell: AkmFDVCell; // la valeur de cellule pour le champ pour cette WidgetLine

    let maxColls: number = 100;
    let nbColls: number = 0;
    let fkd: FeiKeyDef;
    let r: string; // rawKey
    let l: string; // label
    let s: any; // sortKey
    // on boucle sur les lignes existantes, pour alimenter le byColumnPairs
    if(byColumnPairs.size > 0) { // seulement s'il y a des colonnes
      pool.forEach((line: WidgetLine) => {
        [...byColumnPairs.keys()].forEach((columnCode: string) => {
          if(columnCode === ViewData.SCEN_KEY_CODE) { // champ scenar en colonne, toujours OK
            byColumnPairs.get(columnCode).set(line.scen, {r: line.scen, l: line.scen, s:line.scen});
          } else { // un vrai champ
            // gestion des mesures en colonnes
            fkd = keyMap.get(columnCode);
            if(fkd?.isMeasure) { // une mesure en colonne
          
              if(nbColls <= maxColls) { // on limite à 100 colonnes, c'est déjà pas mal 
                r = BaselineEngine.getValue(fei, line, line.cells?.get(columnCode));
                // TODO prise du format
                l = this.model.formatEngine.applyFormat(r, fkd.datakind, byColumnFormats.get(columnCode) || null, null);
                if(fei.model.dataEngine.datakindIsNumeric(fkd.datakind)) {
                  s = Number(r);
                } else {
                  s = l;
                }
                if(!byColumnPairs.get(columnCode).has(r)) {
                  byColumnPairs.get(columnCode).set(r, {r, l, s});  
                  nbColls++;
                }
              }
            } else { // key ou chrono
              cell = BaselineEngine.checkFDVCell(fei, line.bl, columnCode, WizConstants.FIELD_LABEL);    
              if(!!cell?.rawVal) { // la cellule possède bien la prop
                // mais pas encore la sortKey ??
                // TODO TODO en prendre le order si existe, sinon le champ du wcio
                let ord: any = cell.sortKey;
                let sort: any = BaselineEngine.getSort(fei, line, columnCode, WizConstants.FIELD_ORDER);
                if(!!sort) {
                  ord = sort;
                }
                // byColumnPairs.get(columnCode).set(cell.rawVal, {r: cell.rawVal, l: cell.label, s:cell.sortKey});
                byColumnPairs.get(columnCode).set(cell.rawVal, {r: cell.rawVal, l: cell.label, s:ord});
                // console.log('byColumnPairs', columnCode, cell.rawVal, ord);
              }
              
            }     
          }
        });
      });

      // maintenant, on peut construire avec les valeurs présentes
      let keyPairMap: Map<string, KeyPair>;
      let headEntry: ColumnHeaderVO;
      let numericSort: boolean = false;
      context.c.forEach((wcio: WidgetConfigurationItemObj) => {
        // console.log(" buildPivotColumns context.c", wcio);
        // gestion des colonnes en Header, uniquement sur Pivot
        if (keyMap.has(wcio.cod)) { // la colonne doit être valide 
          // console.log('build headers column', wcio.cod);
          keyPairMap = byColumnPairs.get(wcio.cod);
          headEntry = new ColumnHeaderVO(wcio.cod, [], wcio); // fkd.fullcode, coll[keyRawVal, KeyValue, sortKey]
          numericSort = this.model.dataEngine.datakindIsNumeric(keyMap.get(wcio.cod).datakind);
          if(keyPairMap?.size > 0) {
            Array.from(keyPairMap.values()) // attention, si tout est filtré
            .sort((a, b) => WizCollectionUtils.simpleSort(a.s, b.s, numericSort))
            .forEach((kp: KeyPair) => {
              headEntry.keyPairs.push(kp);
            });
          }
          if(wcio.colTot) {
            // console.log('fei engine cols', wcio);
            headEntry.keyPairs.push({r: '@@TOTAL@@', l: wcio.colTotLabel, s:0})
          }

          if (!heads) {
            heads = [];
          }
          heads.push(headEntry); // ajout au tableau de colonnes
        }
      });
    }

    let result: any[];
    let gridHeaders: any[] = []; // contient une collection de "header AG" gigogne (pour alimentation du grid)
    if (heads) {
      // champs en colonne, construction de la grappe de header

      // set<codestrict>, FD ou Chrono utilisés, pour recherche d'une hierarchie
      const headSet: string[] = [];
      let previousHeaderLevel: any[] = []; // le niveau précédement créé
      let currentHeadLevel: any[] = []; // le niveau en cours de création, collection de header
      let idx: number = 1;

      // heads.forEach((head: [string, [string, string, any][]]) => { // [fkd.code], col[keyRawVal, KeyValue]
      heads.forEach((head: ColumnHeaderVO) => {
        // [fkd.code], col[keyRawVal, KeyValue]
        const fkd: FeiKeyDef = keyMap.get(head.keyCode); // le fkd correspondant à fkd.code
        const headVals: KeyPair[] = head.keyPairs; // coll [code, label], les valeurs à dépiler

        headVals.forEach((pair: KeyPair) => {
          let h = this.buildGridHeader(context, fkd, pair, headSet, previousHeaderLevel, head.conf);
          // if(pair.r === '@@TOTAL@@') {
          //   h.pinned = 'right';
          // }
          currentHeadLevel.push(h); 
        });

        if (idx === 1) {
          // ce sont les colonnes "maitresses"
          gridHeaders = currentHeadLevel;
        }
        // le niveau de header est alimenté
        headSet.push(fkd.code); // ajout à l'indicateur de pile de colonnes
        previousHeaderLevel = currentHeadLevel; // on alimente la pile de nouveaux "parents"
        currentHeadLevel = []; // on rince la pile de travail
        idx++;
      });

      // TODO TODO construction du cartesien de colonnes
      // prendre en compte même FD

      // Fin de traitement, on pousse les dataCols dans previousHeaderLevel (le dernier niveau généré)
      previousHeaderLevel.forEach((parentHeader: any) => {
        parentHeader.children = Array.from(dataCols);
        // if(parentHeader.keycode === '@@TOTAL@@') {
        //   parentHeader.children?.forEach(col => {
        //     col.pinned = 'right';
        //   });
        // }
      });

      result = rowCols.concat(gridHeaders);
    } else {
      // pas d'aggrégat en colonnes, on peut essayer les groupes
      if(context.g?.length > 0) {
        dataCols = this.customGroupBuilder(widget, keyMap, context, editionAllowed);
        // console.log('try group on pivot', context.g, dataCols);
      } 
      result = rowCols.concat(dataCols);
      
    }

    //#### patch mode reverse sur les pivots
    if(context.revert) {
      
      rowCols= [];

      // alimentation de la première colonne, figée qui contiendra les mesures
      let fakeMeas: Measure = new Measure();
      fakeMeas.shortCode = ViewData.REVERT_MESURE_COLUMN;
      fakeMeas.code = ViewData.REVERT_MESURE_COLUMN;
      fakeMeas.mode = WizConstants.MEASURE_MODE_SUBMIT;
      

      // prise du contexte de la colonne, pour application des tailles
      let colcontext: WidgetConfigurationItemObj = getDefaultWidgetConfigurationItemObj();
      if(context.c?.length > 0) {
        colcontext = context.c[0];
      }
      // prise du contexte de la colonne de mesures
      let firstContext: WidgetConfigurationItemObj = getDefaultWidgetConfigurationItemObj();
      if(context.d?.length > 0) {
        firstContext = context.d[0];
      }

      let firstSize: number = firstContext.csiz || 150;
      let otherSize: number = colcontext.csiz || 100;

      
      let fakefkd: FeiKeyDef = BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.MEASURE, fakeMeas, WizConstants.MEASURE_MODE_SUBMIT)
      .setParams(true, true, null)
      .setSize(firstSize, 30);

      let fakeWcio = getDefaultWidgetConfigurationItemObj();
      // pas de label sur cette colonne
      fakeWcio.lbl = ' '; // WizTranslationManager.getInstance().translateKey('$.measure.title.$');
      fakeWcio.pin = 'left';
      fakeWcio.csiz = firstSize;
      rowCols.push(this.buildGridColumn(fakefkd, widget, fakeWcio, 'row', false));
      


      // ajout des colonnes pour chaque valeur autorisée
      gridHeaders.forEach(heady => {
        
        fakeMeas = new Measure();
        fakeMeas.code = heady.groupId;
        fakeMeas.shortCode = heady.groupId.split(ViewData.KEY_SEPARATOR)[1];
        fakeMeas.mode = WizConstants.MEASURE_MODE_SUBMIT;

        fakefkd = BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.MEASURE, fakeMeas, WizConstants.MEASURE_MODE_SUBMIT)
        .setParams(true, true, null)
        .setSize(otherSize, 30);
        
        fakeWcio = getDefaultWidgetConfigurationItemObj();
        fakeWcio.lbl = heady.headerName;
        fakeWcio.csiz = otherSize;
        rowCols.push(this.buildGridColumn(fakefkd, widget, fakeWcio, 'data', false));
        
      })
      result = rowCols;
    }



    return result;
  }

  /** retourne le label "technique", ne concerne que les mesures */
  public getFeiKeyDefTechnicalLabel(key: FeiKeyDef): string { 
    if (key.isMeasure) { 
      let result: string = this.model.measureEngine.getMeasureLabel(key.measure);
      result += ' (' + key.measure.shortCode + ViewData.CONTEXT_SEPARATOR + key.mode + ')'
      return result;
    } else {
      return this.getFeiKeyDefLabel(key);
    }
  }

  /** construit le libellé par défaut pour un fdk */
  public getFeiKeyDefLabel(key: FeiKeyDef): string {
    
    if(!key.label) { // existe pas encore, construction
      if (key.isScen) { // 
        key.label =  WizTranslationManager.getInstance().translateKey('$.feature.static.SCENARIO.$');
      }
      else if (key.isMeasure) {  
        let measureVO: MeasureVO = new MeasureVO(key.measure);
        key.label = measureVO.trsLabel + ' (' + key.mode + ')';
      } else { // FD ou Chrono
        if(key.isFilterKey) { // filterKey
          let filterKeyVO: VisionFilterKeyVO = new VisionFilterKeyVO(this.model, key.visionFilterKey);
          key.label = filterKeyVO.trsLabel;
        } else { // visionKey
          let keyVO: VisionKeyVO = new VisionKeyVO(this.model, key.visionKey);
          key.label = keyVO.trsLabel;
        }
      }
    }
    return key.label;
  }

  /**
   * construction d'un param de colonne du grid (colDef)
   * @param key la clé du FEI
   * @param widget le composant pivot source, pour mapper la fonction de calcul des mesures aggrégées (et de toutes les mesures)
   * @param wcio la configuration issues du contexte*
   * @param place: row ou data
   * @param popEditAllowed, autoriser l'accès au crayon pour la popUp de saisie en masse
   * @returns  une colonne de grid
   */
  public buildGridColumn(key: FeiKeyDef, widget: IWidget, wcio: WidgetConfigurationItemObj, place: string, popEditAllowed: boolean = true): any {
       
    const col: WizColDef = {
      fkd: key,
      widget: widget,
      wcio,
      place,
      field: key.fullCode, // FD ou CHRONO et #lvl ou MEASURE#Mode
      headerName: wcio.lbl || this.getFeiKeyDefLabel(key), // label défini dans le contexte, ou par défaut
      hide: false, // pas de gestion des colonnes cachées
      pinned: wcio.pin, // géré par le contexte (param sur wid.r);
      suppressMovable: true, // pas de déplacement des colonnes, utiliser la pop de conf
      enableCellChangeFlash: true, // joli effet sur MAJ...
      editable: false, // pas d'édition par défaut
      enableSorting: true,
      sortable: key.sortable, // gestion du tri
    };

    if (widget.widgetKind === WIDGET_KIND.FD_GRID) { // grid Freedata, surcharge du label de header
      if(wcio.lbl) {
        col.headerName = wcio.lbl;
      } else {
        let fdArg: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(key.code, wcio.fdvf.split(WizConstants.FD_PARTS_SEPARATOR)[0]);
        if(fdArg) {
          col.headerName = fdArg.trsLabel;
        } else {
          col.headerName ='unknown property';
        }
      }
    }

    // colonne masquée ?
    col.hide = !!wcio.hiddenCol;



    if (wcio.agg) {
      // mode pivot, on surcharge le field avec la fonction d'aggrégat
      col.field += ViewData.KEY_SEPARATOR + wcio.agg;
      // col.wizAgregateFunc = wid.agg; // mode pivot, on passe la méthode d'aggrégat, pour override des formats
    }

    // ### configuration de base ###
    col.width = key.width; // taille de la colonne
    if (!!wcio.csiz) {
      col.width = wcio.csiz;
    }

    col.resizable = false;
    if(!!widget.templatingMode) {
      col.resizable = true;
    } else {
      if (!!wcio.casiz) {
        col.resizable = wcio.casiz;
      }
    }

    if(wcio.cauto) { // autosize
      col.flex = 1;
    } else {
      col.flex = null;
    }

    col.minWidth = key.minWidth;
    if (!!wcio.cmsiz) {
      col.minWidth = wcio.cmsiz;
    }

    // gestion de la première colonne permanente (en cas de colonnes groupées)
    //if (place === 'data') {
      col.columnGroupShow = wcio.fdCol ? null : 'open';
    //}


    // ############
    // ############ gestion des colonnes spécifiques ###
    // ############
    // override pour gestion incexc
    if (key.incexcField) {
      // spécifique Include / Exclude, champ avec 2 icones INC / EXC
      col.cellRendererFramework = PivotIncExcRenderer;
      col.resizable = false;
      col.enableCellChangeFlash = false;
      col.headerName = ''; // pas de libellé pour le header
      return col;
    }

    // // override pour gestion vsp
    if (key.vspField) {
      col.field = 'VSP';
      col.resizable = false;
      col.sortable = true;
      col.enableCellChangeFlash = false;
      col.headerName = ''; // pas de libellé pour le header
      col.editable = true;

      col.valueGetter = widget.valueAccessor.bind(widget);
      col.valueFormatter = widget.valueFormatterAccessor.bind(widget);
      col.cellClass = 'cbox-cell';
      col.cellRenderer = WizAgGridUtils.CELL_CHECKBOX_RENDERER;

      col.datakind = FIELD_KIND.BOOLEAN; // on force le type booléen pour la colonne
      wcio.agg = AggregateEngine.AGG_BOOLEAN;
      return col;
    }



    // ### gestion des headers, du tri et de l' alignement ###
    if (!key.deleteField && !key.selectField && !key.incexcField && !key.vspField) {
      col.headerComponentFramework = PivotHeaderRenderer; // rendu unique des headers
      col.headerComponentParams = { enableMenu: (!!widget.settings && widget.settings.sa !== false && popEditAllowed) || !!widget.templatingMode }; // affichage de l'icone de settings TODO prendre wcio.sa
    }


    // ### rendu par défaut
    col.cellRenderer = this.model.gridAccessors.CELL_DEFAULT_RENDERER;

    // ### gestion de la source de données et du formateur ###
    if (key.isMeasure) {
      // champs de mesure
      col.valueGetter = widget.valueAccessor.bind(widget);
      col.valueFormatter = widget.valueFormatterAccessor.bind(widget);
      col.tooltipValueGetter = widget.valueFormatterAccessor.bind(widget);
      col.comparator = MathUtils.sortComparator;
    } else {
      // rendu des visionKeys
      col.valueGetter = widget.keyValueAccessor.bind(widget);
      col.valueFormatter = widget.keyFormatterAccessor.bind(widget);
      col.tooltipValueGetter = widget.keyFormatterAccessor.bind(widget);
      // col.comparator = this.model.gridAccessors.sortComparator;
    }

    let measurePivotKey: boolean = false; // mesure comme clé de pivot. Pas éditable
    if(col.place === 'row' && widget?.context?.pm && col.fkd.isMeasure) {
      measurePivotKey = true; 
    }

    let uniqueColumn: boolean = widget?.context.pmdex && !!this.model.widgetEngine.pmdExParams(widget?.context)?.uniqueColumn;
    if(col.place === 'row' && uniqueColumn) {
      col.cellRenderer = this.model.gridAccessors.CELL_UNIQUECOLUMN_RENDERER;
    }

    // inversion du .ro sur les FDGRID
    let ro: boolean = wcio.ro;
    if(widget?.widgetKind === WIDGET_KIND.FD_GRID) {
      ro = !ro;
    }


    // ### gestion de l'édition (ou pas) ###
    if (widget?.context?.eda !== false && 
      key.editable && 
      ro !== true && 
      popEditAllowed && 
      (widget?.widgetGlobalKind === WIDGET_GLOBAL_KIND.BASELINE || widget?.widgetKind === WIDGET_KIND.FD_GRID) && 
      !measurePivotKey // pas d'édition pour les mesures en clé de pivot
    ) {
      // champ éditables (directement en saisie)
      col.editable = widget.checkBeforeEdit.bind(widget) && (place === 'data' || widget?.widgetKind === WIDGET_KIND.FD_GRID); // key.editable;
      col.valueSetter = widget.updateCell.bind(widget); // méthode de persistence des modifs
      col.cellEditor = 'pivotCellEditor'; // ajouté au framework du grid dans pivotComponent.drawContent; Editeur unique
      col.popEditAllowed = popEditAllowed; // accès à la pop d'édition (pas dans la popUp d'édition...)
      col.cellRenderer = this.model.gridAccessors.CELL_EDITABLE_RENDERER;
      col.cellClass += ' grid-cell-editable';
    }

    // ### gestion du format de données ###
    col.datakind = key.datakind; // par défaut, celui de la mesure / du FDV
    if (wcio.agg) {
      const aggFunc: PivotFunction = this.model.aggregateEngine.functionMap().get(wcio.agg);
      if (aggFunc && aggFunc.dataKind) {
        col.datakind = aggFunc.dataKind; // override par la méthode d'aggrégat
      }
    }

    // ### gestion de l'alignement ###
    //if(!key.editable) { // par pour les cells éditables, ça flingue le scenar
    switch(wcio.heada) {
      case 'L':
        col.cellClass = 'grid-cell-left';
        break;
      case 'C':
        col.cellClass = 'grid-cell-centered';
        break;
      case 'R':
        col.cellClass = 'grid-cell-right';
        break;
      default:
        if (this.model.dataEngine.datakindIsNumeric(col.datakind)) {
          // rendu numérique des cellules simples (alignement droite)
          col.cellClass = 'grid-cell-right';
          col.type = 'numericColumn';
        } else {
          col.cellClass = 'grid-cell-left';
        }
    }

    // gestion du rendu image pour les mesures
    if (col.datakind === FIELD_KIND.BINARY) {
      col.cellClass = 'grid-cell-centered';
      col.cellRenderer = this.model.gridAccessors.attachmentCellRender;
    }

    // gestion du rendu image pour les mesures
    if (col.datakind === FIELD_KIND.COMMENT) {
      col.cellClass += ' grid-multiline';
    }

    // gestion du rendu images multiples
    if (col.datakind === FIELD_KIND.BINARY_MULTI) {
      col.cellClass = 'grid-cell-left';
      col.cellClass += ' grid-cell-overflow';
      col.cellRenderer = this.model.gridAccessors.attachmentMultiCellRender;
    }

    // gestion du rendu couleur pour les mesures
    if (key.datakind === FIELD_KIND.COLOR) {
      col.cellClass = 'grid-cell-special';
      col.cellRenderer = this.model.gridAccessors.colorRenderer;
    }

    // gestion du renderer spécifique checkBox. Toujours si le champ est éditable
    if (key.datakind === FIELD_KIND.BOOLEAN && col.editable) {
      col.cellClass = 'cbox-cell';
      col.cellRenderer = this.model.gridAccessors.CBX_EDITABLE_RENDERER; //  WizAgGridUtils.CELL_CHECKBOX_RENDERER;
      col.datakind = FIELD_KIND.BOOLEAN; // on force le type booléen pour la colonne
      
      if (!!widget.pivotMode) {
        // en mode pivot, on force aussi la méthode d'aggrégat
        wcio.agg = AggregateEngine.AGG_BOOLEAN;
      }
    } else {
      // pas un booléen éditable
      if (col.datakind === FIELD_KIND.BOOLEAN) {
        // mais un rendu booléen
        col.cellClass = 'cbox-cell';
        col.cellClass += ' no-edit';
        col.cellRenderer = WizAgGridUtils.CELL_CHECKBOX_RENDERER;
      }
    }

    // ### gestion du champs de rendu pour les FDVs ###
    if (wcio.fdvf) {
      col.fdvfield = wcio.fdvf; // présence d'un format dans le contexte
      let realField: string  = this.model.widgetEngine.fdvfSplitter(wcio.fdvf)[0]; 
      const arg = this.model.freedataEngine.getArgFromFD(key.code, realField);
      if (arg) {
        col.dataformat = arg.format;

        let finalType: string = arg.dataType;
        if(arg.dataType === FIELD_KIND.FORMULA) { // pour les formules, on prend le type du format (s'il existe)
          finalType = FIELD_KIND.STRING;
          try {
            let format = arg.format;
            // console.log('formula format', format);
            let formatVO = this.model.formatEngine.getFormat(format.split(ViewData.KEY_SEPARATOR)[0], format.split(ViewData.KEY_SEPARATOR)[1]);
            finalType = formatVO.dataKind;
          } catch(e: any) {
  
          }
        }   

        if(arg.dataType === FIELD_KIND.OBJECT) { // possible sous-objet avec subField
          let realSubField: string  = this.model.widgetEngine.fdvfSplitter(wcio.fdvf)[1];
          if(realSubField) {
            let realFD: FreeDataVO = this.model.freedataEngine.getFreeData(arg.datas.split(WizConstants.CONTEXT_SEPARATOR)[0]);
            let realArg: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(realFD?.code, realSubField);
            finalType = realArg?.dataType;
            // console.log('sub Final type', finalType, realFD?.code);
          } 
        }  

        // console.log('formula final', finalType);
        col.datakind = finalType;
        if (this.model.dataEngine.datakindIsNumeric(col.datakind)) {
          // rendu numérique des cellules simples (alignement droite)
          col.type = 'numericColumn';
        }

        if (col.datakind === FIELD_KIND.BINARY) {
          col.cellClass = 'grid-cell-centered';
          col.cellRenderer = this.model.gridAccessors.attachmentCellRender;
          col.tooltipField = null;
        }     // gestion du rendu images multiples
        else if (col.datakind === FIELD_KIND.BINARY_MULTI) {
          col.cellClass = 'grid-cell-left';
          col.cellClass += ' grid-cell-overflow';
          col.cellRenderer = this.model.gridAccessors.attachmentMultiCellRender;
        } else if (col.datakind === FIELD_KIND.BOOLEAN) {
          col.cellClass = 'cbox-cell';
          col.cellClass += ' no-edit';
          col.cellRenderer = WizAgGridUtils.CELL_CHECKBOX_RENDERER;
        } else if (col.datakind === FIELD_KIND.COLOR) {
          col.cellClass = 'grid-cell-centered';
          col.cellClass += ' no-edit';
          col.cellRenderer = this.model.gridAccessors.colorRenderer;
        }

      }

    }

    // ### gestion des styles ###
    if (!!wcio.sty || !!wcio.csty) {
      // présence d'un style dans le contexte
      col.styleRenderer = wcio.sty;
      col.defaultStyleRenderer = wcio.csty;
      col.cellStyle = this.model.styleEngine.styleRenderer.bind(this.model.styleEngine);
    } else {
      col.cellStyle = this.model.styleEngine.noCssRenderer.bind(this.model.styleEngine);
    }

    col.defaultAggStyleRenderer = wcio.hsty;

    // ### gestion des formats ###
    if (wcio.fmt) {
      // présence d'un format dans le contexte
      col.dataformat = wcio.fmt;
    } else if (!col.dataformat && !!key.defaultFormat) { // pas de format (ni par wci, ni par format de field)
      // ou d'un format par défaut
      col.dataformat = key.defaultFormat;
    }


    // le format peut écraser le type de col...
    // patch rendu media / hyperlink sur datakind = TEXT
    // gestion du rendu image pour les mesures
    if (col.datakind === FIELD_KIND.STRING && !!wcio.fmt) {
      const twoPart: string = wcio.fmt.split(ViewData.KEY_SEPARATOR)[1];
      if (twoPart === 'MEDIA') {
        // console.log('fei string to media', col);
        col.cellClass = 'grid-cell-centered';
        col.cellRenderer = this.model.gridAccessors.imageCellRender;
        col.tooltipField = null;
      } else if (twoPart === 'HYPER') {
        // console.log('fei string to link', col);
        col.cellRenderer = this.model.gridAccessors.hyperlinkCellRender;
      }
    }

    // gestion des rendus de type action
    if (col.datakind === FIELD_KIND.ACTION) {
      // console.log('FEIvo actionRenderer', col);
      col.cellClass = 'grid-cell-centered';
      col.valueGetter = this.model.gridAccessors.actionAccessor;
      col.cellRenderer = this.model.gridAccessors.actionCellRender;
      col.tooltipField = 'trsDescription'
    }

    // gestion du rendu File
    if (col.datakind === FIELD_KIND.FILE) {
      col.editable = true && place === 'data';
      col.cellRenderer = this.model.gridAccessors.fileCellRender;
    }

    // gestion du rendu image pour les mesures
    if (col.datakind === FIELD_KIND.LIST) {
      col.cellRenderer = this.model.gridAccessors.CELL_EDITABLE_RENDERER; // listCellRender;
    }

    // gestion du rendu image pour les messages workcom
    if (col.datakind === FIELD_KIND.WORKFLOW) {
      col.cellRenderer = this.model.gridAccessors.workflowCellRender;
      col.tooltipValueGetter = widget.tooltipAccessor.bind(widget);
    }

    //### patch pivot.revert
    if(widget?.context?.revert) {
      col.cellRenderer = this.model.gridAccessors.CELL_REVERT_RENDERER;
      col.headerComponentParams.enableMenu = false;
      col.cellStyle = this.model.styleEngine.styleRevertRenderer.bind(this.model.styleEngine);
    }


    return col;
  }

  /**
   * construction des entêtes en cas de groupes ou de champs en colonne
   * @param context pour prise du open / close
   * @param key le fkd pour ce niveau
   * @param pair: keyPair, [code, label, sortKey] pour la colonne en cours
   * @param headSet: Set<string>  la pile de niveaux de header déjà créés
   * @param previousLevel les headers (tous) du niveau précédent
   * @param contextItem: WidgetConfigurationItemObj
   * @returns une entrée de header pour ce niveau
   */
  private buildGridHeader(
    context: WidgetConfigurationObj,
    key: FeiKeyDef,
    pair: KeyPair,
    headPile: string[], // type de champ dans le header, pour recherche d'héritage
    previousLevel: any[], // clés du niveau précédent
    contextItem: WidgetConfigurationItemObj,
  ): any {
    const head: any = {};
    // col.field = key.fullCode;

    head.headerName = this.getFeiKeyDefLabel(key) + ' : ';
    if(!!contextItem?.lbl) {
      if(contextItem?.lbl === ViewData.NONE) {
        head.headerName = '';
      } else {
        head.headerName = contextItem.lbl + ' : ';
      }
    }

    if (!!contextItem?.fdvf) { // si on utilise un autre champ du FDV

      if (key.isFreedata) { // je recherche un sous-champ sur un FDV (donc le code d'un autre FDV)
        const arg = this.model.freedataEngine.getArgFromFD(key.code, contextItem.fdvf); // on prend la conf du champ
        if (!!arg) {
          
          let val: string = pair.l; // on prend le label sur le keypair

          if (arg.dataType === FIELD_KIND.BINARY) { // c'est une image
            head.binary = val; // c'est le blobID
          } else { // sinon, champ simple. On va le formater (les FD sont passés en datakind = object)

            // pas forcément prendre le label... 
            let fdv: FreedataValueVO = this.model.freedataEngine.getFreedataValue(key.code, pair.r);
            let valBis = this.model.freedataEngine.getFDValue(fdv, arg.code);
            // console.log('val',fdv?.code, val, valBis);
            if(valBis) {
              val = valBis;
            }

            let realFormat: string = contextItem?.fmt || arg.format;
            head.headerName += this.model.formatEngine.applyFormat(val, arg.dataType, realFormat, null);
          }
        } else { // pas trouvé le arg correspondant... champ supprimé du FD / FDV
          head.headerName += pair.l; // dto.pairs[contextItem.fdvf];
        }
      } else if (key.isChrono) { // pour les chronos, mef du sortKey
        // TODO prise du bon champ
        let chrono: ChronocodeVO = this.model.chronocodeEngine.getChronocode(pair.r);
        let chronoKind: string = this.model.chronocodeEngine.getChronoFieldByCode(contextItem.fdvf).datakind;
        let chronoFormat: string = contextItem?.fmt || null;
        if(!!chrono) {
          let realValue = this.model.chronocodeEngine.getChronoValue(chrono, contextItem.fdvf);
          
          // console.log('chrono', chrono, chronoKind, chronoFormat, realValue);
          head.headerName += this.model.formatEngine.applyFormat(realValue, chronoKind, chronoFormat, null);
        } else { // pas trouvé, on prend le libellé
          head.headerName += pair.l;
        }    
     } 

    } else { // sinon
      if (contextItem?.fmt) { // si un format est défini
        head.headerName += this.model.formatEngine.applyFormat(pair.r, key.datakind, contextItem.fmt, null);
      } else {
        head.headerName += pair.l;
      }
    }

    head.headerGroupComponent = 'pivotHeaderGroupRenderer';


    head.style = contextItem?.hsty; // prise du style
    // head.headColorField = contextItem?.headColorField;// rendu sur field
    head.textAlign = contextItem?.heada; // prise de l'alignement

    // gestion des couleurs custom sur les pivot.headers
    let backOverride: string = null;
    if(contextItem?.headColorField) { // même traitement que contextItem?.fdvf
      // console.log('now override style', contextItem?.headColorField);
      if(key.isScen) {
        let scen: ScenarioVO = this.model.scenarioEngine.getScenario(this.model.currentVision?.code, pair.r);
        backOverride = scen?.color || null;
      }
      else if (key.isFreedata) { // je recherche un sous-champ sur un FDV (donc le code d'un autre FDV)     
        let fdv: FreedataValueVO = this.model.freedataEngine.getFreedataValue(key.code, pair.r);
        backOverride = this.model.freedataEngine.getFDValue(fdv, contextItem.headColorField);
        // console.log('now override style2', backOverride, head.style);

      } else if (key.isChrono) { // pour les chronos, mef du sortKey
        // TODO prise du bon champ
        let chrono: ChronocodeVO = this.model.chronocodeEngine.getChronocode(pair.r);
        if(!!chrono) {
          backOverride = this.model.chronocodeEngine.getChronoValue(chrono, contextItem.headColorField);
          // console.log('now override style3', backOverride, head.style);
        }    
     } 
    }

    if(!!backOverride) {
      let styleParts: string[] = [];
      if(!!head.style) {
        styleParts = head.style.split(WizConstants.KEY_SEPARATOR);
      } else {
          styleParts.push(StyleEngine.STYLE_DEFAULT_BACKGROUND_COLOR); // couleur de background par défaut pour un header de colonne
          styleParts.push(StyleEngine.STYLE_DEFAULT_FONT_COLOR); // couleur de background par défaut pour un header de colonne
      }
      styleParts[0] = backOverride;
      head.style = styleParts.join(WizConstants.KEY_SEPARATOR);
    }

    // gestion des sous-titres sur les pivot.headers
    if(contextItem?.headSubField) { // même traitement que contextItem?.fdvf
      // console.log('now override style', contextItem?.headColorField);
      if (key.isFreedata) { // je recherche un sous-champ sur un FDV (donc le code d'un autre FDV)     
        
        const arg = this.model.freedataEngine.getArgFromFD(key.code, contextItem.headSubField); // on prend la conf du champ
        if (!!arg) {
          
            // pas forcément prendre le label... 
            let fdv: FreedataValueVO = this.model.freedataEngine.getFreedataValue(key.code, pair.r);
            let val = this.model.freedataEngine.getFDValue(fdv, arg.code);
            head.headSubField = this.model.formatEngine.applyFormat(val, arg.dataType, null, null);
          }
      } else if (key.isChrono) { // pour les chronos, mef du sortKey
        // TODO prise du bon champ
        let chrono: ChronocodeVO = this.model.chronocodeEngine.getChronocode(pair.r);
        let chronoKind: string = this.model.chronocodeEngine.getChronoFieldByCode(contextItem.headSubField).datakind;
        
        if(!!chrono) {
          let realValue = this.model.chronocodeEngine.getChronoValue(chrono, contextItem.headSubField);
          head.headSubField =  this.model.formatEngine.applyFormat(realValue, chronoKind, null, null);
        }    
     } 
    }



    // le libellé du fkd + la valeur de la clé;
    head.groupId = key.fullCode + ViewData.KEY_SEPARATOR + pair.r; // la clé du fkd + le code de l'élément

    if (!!context.gps) { // marquer les colonnes expanded
      context.gps.forEach(item => {
        if (item.groupId === head.groupId) {
          // console.log('exp', item);
          head.expanded = item.open;
        }
      });
    }

    // champs custom pour traitement
    head.fkd = key; // on trace le fkd
    head.keycode = pair.r; // .rawValue;
    // sous la forme [codeStrict, level, rawKey]
    head.headPile = [[key.code, key.level, pair.r]]; // la pile (inversée) de clé pour ce header

    // on va s'ajouter dans le children du niveau précédent
    if (previousLevel.length > 0) {
      // S'il existe
      previousLevel.forEach((parentHeader: any) => {
        let iamIn: boolean = true; // par défaut, on me garde
        let headSet: Set<string> = new Set(headPile);
        if (headSet.has(key.code)) {
          // si je suis une clé, je check les parents dans la pile

          // chercher X levels
          parentHeader.headPile.forEach((item: [string, number, string]) => {
            if (item[0] === key.code) {
              // même base de chrono ou FD
              if(key.isChrono) { // structure normalisée pour les chronos
                if (head.keycode.indexOf(item[2]) < 0) {
                  // je ne fais pas partie de sa clé
                  iamIn = false; // je sors
                  // console.log('iamOut', item[2],  head.keycode);
                }
              } else {
                iamIn = this.isSameColumn(key, item[2], head.keycode);
              }
       
            }
          });
        }

        if (iamIn) {
          // pas filtré par les headers précédents si gigogne
          const parentChildren: any[] = parentHeader.children;
          head.headPile = head.headPile.concat(parentHeader.headPile); // construction de ma pile de header
          if (!!parentChildren) {
            head.columnGroupShow = 'open'; // pas le premier, visible seulement si expand
            parentChildren.push(head);
          } else {
            head.columnGroupShow = null; // je suis le premier, toujours visible
            parentHeader.children = [head];
          }
        }
      });
    }
    // console.log('fei engine', head);
    return head;
  }

  /**
   * 
   * construction des colonnes de détail sur édition / suppression en masse: tous les champs de clé en ligne
   * et le champ édité en data (si édition)
   * @param fei : le feiVO source de données
   * @param widget: un clone du widget d'origine. On passe son pivotMode à false, et on écrase son contexte
   * @param field : le champ cible pour maj
   * @return une collection de colonnes AgGrid
   */
  public buildColumnsForMassEdit(fei: FeiVO, widget: IWidget, field: string = null): any[] {
    const context: WidgetConfigurationObj = {
      ...getDefaultWidgetConfigurationObj(),
      v: fei.visionCode,
      vs: fei.visionStepLevel,
      vsp: '', // TODO gestion des stepPerimeters
      pm: false, // mode pivot
      r: [],
      c: [],
      f: [],
      d: [],
    };

    // passage en mode simple
    widget.pivotMode = false;

    fei.keys
      .filter((item) => item.isKey && !item.isFilterKey && !item.isScen)
      .sort((a, b) => (a.order >= b.order ? 1 : -1))
      .forEach((item: FeiKeyDef) => { // par défaut, quelques clés et mesures visibles
        const confItem = this.model.widgetEngine.newWcio(item.fullCode, item.order);
        confItem.pin = item.pinned;
        this.model.widgetEngine.wcioColParams(confItem, true); // taille de col
        context.r.push(confItem);
      });
    if (!!field) { // pas de field lors d'une suppression en masse
      context.d.push(this.model.widgetEngine.newWcio(field, 0));
    }

    // widget.context = context;
    return this.buildGridColumns(widget, context, false);
  }

  /* ##############################################################################
              phase de génération: clés, contrôles
     ############################################################################## */

  /**
   * prépare le [] des fonctions de contrôle sur un fei
   * (depuis sa matrice de contrôle)
   * @param fei 
   */
  public builOrderedControls(fei: FeiVO): Measure[] {
    let orderedControls = [];
      
    fei.orderedMatrix?.forEach((mm: MeasMode) => {
      if (mm.mm === WizConstants.MEASURE_MODE_INTERACT) { // seulement sur les I
        const fkd: FeiKeyDef = BaselineEngine.getKey(fei, mm.code);
        // console.log('control fkd', fkd);
        const meas: Measure = fkd.measure;
        if (meas.minValue !== null || meas.maxValue !== null || !!meas.valueControl) {
          orderedControls.push(meas);
        }         
      }

    });
    return orderedControls;
  }

  /**
   * va tenter d'alimenter les colonnes custom
   * @param widget
   * @param keyMap 
   * @param context 
   * @param customHeader 
   * @param customHeaderMap 
   * @param dataCols pour leur assigner le groupe parent
   * @param editionAllowed
   * @return les colonnes pour la partie "row"
   */
  private customRowGroupBuilder(widget: IWidget, keyMap: Map<string, FeiKeyDef>, 
    context: WidgetConfigurationObj, editionAllowed: boolean): any[]
    {
        let customRowHeader: any[] = []; // colonnes custom
        let customRowHeaderMap: Map<string, any> = new Map();
        let validHeader: Set<string> = new Set();
        let result: any[] = [];
        // création des groupes
        if (context.g?.length > 0) { 
          //customHeader = []; // contient une collection de "header AG" gigogne (pour alimentation du grid)
          const pendingHeader = []; // les groupes membres d'un groupe, on attend papa
          //customHeaderMap = new Map();
          context.g.forEach((wcio: WidgetConfigurationItemObj) => {
            const head: any = {};
            head.headerName = wcio.lbl ? wcio.lbl : wcio.cod;
            head.headerGroupComponent = 'pivotHeaderGroupRenderer';
            head.style = wcio.hsty; // prise du style
            head.textAlign = wcio.heada; // prise de l'alignement
            
            head.fdvfield = wcio.fdvf; // présence d'un champ de rendu
            head.fmt = wcio.fmt; // présence d'un format dans le contexte

            // le libellé du fkd + la valeur de la clé;
            head.groupId = wcio.cod;
            head.columnGroupShow = 'open'; // par défaut pas visible en mode closed
            head.children = [];
            if (!!context.gps) { // marquer les colonnes expanded
              context.gps.forEach(item => {
                if (item.groupId === head.groupId) {
                  // console.log('exp', item);
                  head.expanded = item.open;
                }
              });
            }

            if (!!wcio.pgrp) { // mon groupe dépend d'un groupe, j'ajoute pas tout de suite
              head.parentID = wcio.pgrp;
              pendingHeader.push(head);
            } else {
              customRowHeader.push(head);
            }
            customRowHeaderMap.set(wcio.cod.split(ViewData.CONTEXT_SEPARATOR)[0], head);
          });

          // 2ème passe, on alimente les sous-groupes
          if (pendingHeader.length > 0) {
            pendingHeader.forEach(child => {
              const daddy = customRowHeaderMap.get(child.parentID);
              if (!daddy) {
                // console.log('col no dad', child.parentID, customHeaderMap);
              } else {
                if(daddy.children.length === 0) {
                  child.columnGroupShow = null; // je suis le premier, toujours visible
                }
                daddy.children.push(child);
              }
            });
          }
        }

        // flag pour ajout d'une seule colonne permanente sur les groupes
        let fdCol: boolean = true;
        
        // création des colonnes de données. Potentiellement inféodées à des Header (si champs en colonne)
        context.r.forEach((wcio: WidgetConfigurationItemObj) => {
            if (keyMap.has(wcio.cod)) {
              wcio.fdCol = fdCol; // colonne expandable
              fdCol = false; // une seule
              const col: any = this.buildGridColumn(keyMap.get(wcio.cod), widget, wcio, 'row', editionAllowed);
              col.custom = true;
              if (customRowHeader.length > 0) {//} && !widget.pivotMode) {
                const head: any = customRowHeaderMap.get(wcio.pgrp);
                if (!!head) {
                  if(!validHeader.has(wcio.pgrp)) { // TODO TODO en cas de multi-level ajouter papa
                    // console.log('validHeader', head, wcio);
                    let realHead: any = head; // possible x level, on remonte
                    while(realHead.parentID) {
                      realHead = customRowHeaderMap.get(realHead.parentID);
                    }
                    if(!validHeader.has(realHead.groupId)) {
                      validHeader.add(realHead.groupId);
                      result.push(realHead);
                    }

                    // if(!!head.parentID)
                    // validHeader.add(wcio.pgrp);
                    
                  }
                  
                  if (head.children.length === 0) {
                    col.columnGroupShow = null;
                  } else {
                    col.columnGroupShow = 'open';
                  }
                  head.children.push(col);
                } else {
                  result.push(col);
                }
              } else {
                result.push(col);
              }
            }
        });

        // TODO TODO garder les lignes orphelines & les groupes avec des enfants
        return result;
    }

    /**
     * 
     * @param fkd pile d'héritage d'un FDV pour construction des colonnes
     * @param daddyShortCode 
     * @param childShortCode 
     * @returns 
     */
    private isSameColumn(fkd: FeiKeyDef, daddyShortCode: string, childShortCode: string): boolean {
      let result: boolean = false;
      let fdv: FreedataValueVO = this.model.freedataEngine.getFreedataValueByShortCode(fkd.code, childShortCode);
      while(fdv.parentShortCode && !result) {
        if(fdv.parentShortCode === daddyShortCode) {
          result = true;
        } else {
          fdv = this.model.freedataEngine.getFreedataValueByShortCode(fkd.code, fdv.parentShortCode)
        }
      }

      return result;

    }

}
